import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useForm,
  Controller,
  useFieldArray,
  useWatch,
  Control,
  FieldArrayWithId,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Chip,
  Card,
  CardContent,
  Switch,
  Divider,
  FormControlLabel,
  IconButton,
  Tooltip,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import {
  NumericEntryField,
  NumericDisplayField,
} from "../../components/common/CustomFields";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  Print as PrintIcon,
  ContentCopy,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  useTSQSalesOrder,
  useTSMCreateSalesOrder,
  useTSMPostProcessSalesOrder,
  useTSMUpdateSalesOrder,
  useTSMFulfillSalesOrder,
  useTSMStatusUpdateSO,
  useTSMCompleteSO,
  useTSMVoidSO,
  useTSMCheckForDupePO,
} from "../../hooks/useTSSalesOrder";
import { useTSQAddressList } from "../../hooks/useTSAddresses";
import { useTSQItemList } from "../../hooks/useTSItems";
import { useTSQLocationList } from "../../hooks/useTSLocations";
import { useTSQUOMUnitList } from "../../hooks/useTSUnits";
import { useTSQInvPositionFulfillment } from "../../hooks/useTSInvPosition";
import { useTSQCompanyList, useTSQPeopleList } from "../../hooks/useTSEntities";
import { ShippingMethods } from "../../types/common/termTypes";
import {
  PAYMENT_TERMS,
  PaymentTermType,
  CUST_SHIP_BILLING_TYPES,
  CustomerShipmentBillingType,
} from "../../types/common/termTypes";
import { InvMovCreate } from "../../types/invMovementTypes";
import { OrderAddressEdit } from "../../types/addressTypes";
import { CustomerFull } from "../../types/companyTypes";
import {
  SalesOrderCreate,
  SalesOrderUpdate,
  SalesOrderStatusUpdate,
  SalesOrderFull,
  SOLineCreate,
  SOLineUpdate,
  SOLineFulfillment,
  SOFulfillment,
} from "../../types/salesOrderTypes";
import {
  TypedSelectOption,
  AddressOption,
} from "../../types/common/selectOptions";
import stringUtils from "../../utils/stringUtils";
import { AddressFieldOld } from "../../components/common/AddressViewEdit";
import OrderStatusActions from "../../components/common/OrderStatusActionsPanel";
import { useWorldLocationData } from "../../contexts/WorldLocationContextProvider";
import statusUtils from "../../utils/statusUtils";
import "../../styles/chipOverrides";

type ShippingProviderOption = TypedSelectOption<"shipping_provider">;

const shippingProviderOptions: ShippingProviderOption[] = [
  { type: "shipping_provider", label: "FedEx", value: { id: "FedEx" } },
  { type: "shipping_provider", label: "UPS", value: { id: "UPS" } },
  { type: "shipping_provider", label: "Any", value: { id: "Any" } },
];

type ShipmentMethodOption = TypedSelectOption<
  "shipment_method",
  { provider: string }
>;

type shipMethodKey = keyof typeof ShippingMethods;
const shippingMethodOptions: ShipmentMethodOption[] = (() => {
  let opts: ShipmentMethodOption[] = [];
  for (let key of Object.keys(ShippingMethods)) {
    const label = ShippingMethods[key as shipMethodKey]["label"];
    for (let method of ShippingMethods[key as shipMethodKey]["methods"]) {
      opts.push({
        label: `${label} - ${method}`,
        type: "shipment_method",
        value: {
          id: `${key} ${method}`.toUpperCase(),
          provider: key.toUpperCase(),
        },
      });
    }
  }
  return opts;
})();

type PaymentTermOption = TypedSelectOption<"payment_term">;

const paymentTermOptions: PaymentTermOption[] = (() => {
  return PAYMENT_TERMS.filter((a) => a !== "UNKNOWN")
    .sort((a, b) => a.localeCompare(b))
    .map((val) => ({
      label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
      type: "payment_term",
      value: { id: val },
    }));
})();
type CustomerShipmentBillingOption = TypedSelectOption<"shipment_billing">;

const customerShipmentBillingOptions: CustomerShipmentBillingOption[] = (() => {
  return CUST_SHIP_BILLING_TYPES.filter((a) => true)
    .sort((a, b) => a.localeCompare(b))
    .map((val) => ({
      label: stringUtils.capFirstLetter(val),
      type: "shipment_billing",
      value: { id: val },
    }));
})();

type CustomerOption = TypedSelectOption<
  "customer",
  {
    contact_id: string | null;
    bill_id: string | null;
    ship_id: string | null;
    rep_id: string | null;
    terms: string;
    ship_bill: string | null;
    ship_provider: string | null;
    ship_method: { fedex: string | null; ups: string | null } | null;
  }
>;
type LocationOption = TypedSelectOption<"location">;
type AAddressOption = AddressOption<{ addr_type: string }>;
type SalesRepOption = TypedSelectOption<"sales_rep", { name: string }>;
type ContactOption = TypedSelectOption<"contact">;
type ItemOption = TypedSelectOption<
  "item",
  { number: string; name: string; uomTypeId: string; sales_uom: string }
>;
type UOMOption = TypedSelectOption<
  "unit",
  { symbol: string; conversion: number; typeId: string }
>;
type PositionOption = TypedSelectOption<
  "inv_pos",
  { lot: string; lotId: string; lotExpiration: string | null; quantity: number }
>;

interface PrevFillData {
  lot: string;
  quantity: number;
  unit: string;
  expiration: string | null;
}

interface NewFillData {
  position: PositionOption;
  quantity: number;
  uom: UOMOption;
}

type newFillKey = keyof NewFillData;

interface SOLineData {
  id: string | null;
  quantity_ordered: number | null;
  quantity_fulfilled: number | null;
  unit_cost: number | null;
  notes: string | null;
  item: ItemOption | null;
  uom_unit: UOMOption | null;
  is_delete: boolean;
  is_new: boolean;
  requires_fulfillment: boolean;
  eval_line: boolean;
  mark_complete: boolean;
  line_completed: boolean;
  fulfillments: PrevFillData[];
  new_fills: NewFillData[];
}

interface SOFormData {
  number: string | null;
  order_date: string | null;
  requested_date: string | null;
  customer_po: string | null;
  order_status: string | null;
  approval_status: string | null;
  fulfillment_status: string | null;
  rush_order: boolean;
  tax_rate: number;
  ship_cost: number;
  notes: string | null;
  order_terms: PaymentTermOption | null;
  customer: CustomerOption | null;
  location: LocationOption | null;
  ship_billing: CustomerShipmentBillingOption | null;
  ship_method: ShipmentMethodOption | null;
  bill_addr: AAddressOption | null;
  ship_addr: AAddressOption | null;
  sales_rep: SalesRepOption | null;
  customer_contact: ContactOption | null;
  line_items: SOLineData[];
  create_shipment: boolean;
}
// Validation schema
const salesOrderSchema = yup.object({
  number: yup.string().nullable().defined(),
  order_terms: yup
    .object({
      type: yup.string().required(),
      label: yup.string().required(),
      value: yup.object({
        id: yup.string().required(),
      }),
    })
    .required(),
  order_date: yup.string().required(),
  requested_date: yup.string().required(),
  order_status: yup.string().optional(),
  approval_status: yup.string().optional(),
  fulfillment_status: yup.string().optional(),
  rush_order: yup.boolean().required(),
  ship_cost: yup.number().required(),
  tax_rate: yup.number().required(),
  notes: yup.string().nullable().defined(),
  customer: yup
    .object({
      type: yup.string().required(),
      label: yup.string().required(),
      value: yup.object({
        id: yup.string().required(),
      }),
    })
    .required(),
  location: yup
    .object({
      type: yup.string().required(),
      label: yup.string().required(),
      value: yup.object({ id: yup.string().required() }).required(),
    })
    .required(),
  ship_billing: yup
    .object({
      type: yup.string().required(),
      label: yup.string().required(),
      value: yup.object({ id: yup.string().required() }).required(),
    })
    .required(),
  line_items: yup.array().of(
    yup.object({
      quantity_ordered: yup.number().moreThan(0).required(),
      quantity_fulfilled: yup.number().optional(),
      unit_cost: yup.number().required(),
      notes: yup.string().nullable(),
      item: yup
        .object({
          type: yup.string().required(),
          label: yup.string().required(),
          value: yup.object({ id: yup.string().required() }).required(),
        })
        .required(),
      uom_unit: yup
        .object({
          type: yup.string().required(),
          label: yup.string().required(),
          value: yup.object({ id: yup.string().required() }).required(),
        })
        .required(),
      requires_fulfillment: yup.boolean().required(),
      eval_line: yup.boolean().required(),
      is_delete: yup.boolean().required(),
      is_new: yup.boolean().required(),
      new_fills: yup.array().of(
        yup.object({
          position: yup
            .object({
              type: yup.string().required(),
              label: yup.string().required(),
              value: yup.object({ id: yup.string().required() }).required(),
            })
            .required(),
          quantity: yup.number().moreThan(0).required(),
          uom: yup
            .object({
              type: yup.string().required(),
              label: yup.string().required(),
              value: yup.object({ id: yup.string().required() }).required(),
            })
            .required(),
        }),
      ),
    }),
  ),
});

interface SOPrintData {
  number: string;
  orderDate: string;
  ackShipDate: string;
  requestDate?: string;
  custPO: string;
  shipMethod: string;
  pricing: {
    sub: string;
    tax: string;
    ship: string;
    total: string;
  };
  terms: string;
  customer: {
    name: string;
    code: string;
    billing: {
      name: string;
      addressee: string;
      attn: string;
      line1: string;
      line2?: string;
      line3?: string;
      city: string;
      state: string;
      country: string;
      zip: string;
    };
    shipping: {
      name: string;
      addressee: string;
      attn: string;
      line1: string;
      line2?: string;
      line3?: string;
      city: string;
      state: string;
      country: string;
      zip: string;
    };
    contact: {
      first: string;
      last: string;
      phone?: string;
      email?: string;
    };
  };
  items: {
    number: string;
    name: string;
    quantity: string;
    uom: string;
    rate: string;
    total: string;
    eval: boolean;
  }[];
}
const generateSalesOrderHTML = (data: SOPrintData) => {
  return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <link
      href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;700&display=swap"
      rel="stylesheet"
    />
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Sales Order ${data.number}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: Arial, sans-serif;
            line-height: 1.4;
            color: #333;
            background: white;
            padding: 20px;
        }
        
        .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
        }
        
        .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 30px;
            border-bottom: 2px solid #ddd;
            padding-bottom: 20px;
        }
        
        .company-info {
            display: flex;
            align-items: center;
        }
        
        .logo {
            color: #008675;
            padding: 10px 15px;
            font-size: 48px;
            font-weight: bold;
            font-family: Montserrat;
            margin-right: 20px;
        }
        
        .company-details {
            font-size: 12px;
            line-height: 1.3;
        }
        
        .document-info {
            text-align: right;
        }
        
        .document-title {
            font-size: 28px;
            font-weight: bold;
            margin-bottom: 8px;
        }
        
        .document-number {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 4px;
        }
        
        .addresses {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
            margin-bottom: 25px;
        }
        
        .address-section h3 {
            font-weight: bold;
            margin-bottom: 8px;
            font-size: 14px;
        }
        
        .address-section {
            font-size: 12px;
            line-height: 1.4;
        }

        .details-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 25px;
            font-size: 12px;
        }
        
        .details-table th,
        .details-table td {
            border: 1px solid #666;
            padding: 8px;
            text-align: center;
        }
        
        .details-table th {
            background-color: #f0f0f0;
            font-weight: bold;
        }
        
        .order-details {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 20px;
            margin-bottom: 25px;
            font-size: 12px;
        }
        
        .order-details > div {
            text-align: center;
        }
        
        .order-details strong {
            display: block;
            font-weight: bold;
            margin-bottom: 4px;
        }
        
        .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 25px;
            font-size: 12px;
        }
        
        .items-table th,
        .items-table td {
            border: 1px solid #666;
            padding: 8px;
            text-align: left;
        }
        
        .items-table th {
            background-color: #f0f0f0;
            font-weight: bold;
        }
        
        .items-table .quantity,
        .items-table .uom,
        .items-table .unit-price,
        .items-table .extended-price {
            text-align: right;
        }
        
        .items-table .quantity,
        .items-table .uom {
            text-align: center;
        }
        
        .item-code {
            font-weight: 600;
        }
        
        .item-description {
            font-size: 11px;
            color: #666;
        }
        
        .totals {
            margin-left: auto;
            width: 250px;
        }
        
        .totals table {
            width: 100%;
            font-size: 12px;
        }
        
        .totals td {
            padding: 6px 12px;
            background-color: #e8e8e8;
        }
        
        .totals .label {
            text-align: right;
            font-weight: bold;
        }
        
        .totals .amount {
            text-align: right;
        }
        
        .totals .total-row .amount {
            font-weight: bold;
        }
        
        .footer {
            margin-top: 60px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
        }
        
        .barcode {
            text-align: center;
        }
        
        .barcode-graphic {
            font-family: monospace;
            font-size: 10px;
            background: black;
            color: white;
            padding: 4px 8px;
            display: inline-block;
            letter-spacing: 1px;
        }
        
        .barcode-text {
            font-size: 10px;
            margin-top: 4px;
        }
        
        .page-info {
            font-size: 10px;
            color: #666;
        }
        
        .print-controls {
            position: fixed;
            top: 10px;
            right: 10px;
            background: white;
            padding: 10px;
            border: 1px solid #ddd;
            border-radius: 4px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            z-index: 1000;
        }
        
        .print-controls button {
            margin: 0 5px;
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
        }
        
        .print-btn {
            background: #008675;
            color: white;
        }
        
        .print-btn:hover {
            background: #006b5a;
        }

        .mode-btn {
            background: #4CAF50;
            color: white;
        }

        .mode-btn:hover {
            background: #45a049;
        }
        
        .close-btn {
            background: #666;
            color: white;
        }
        
        .close-btn:hover {
            background: #555;
        }
        
        /* Print styles */
        @media print {
            .print-controls {
                display: none !important;
            }
            
            body {
                padding: 0;
            }
            
            @page {
                margin: 0.5in;
                size: letter;
            }
        }
    </style>
</head>
<body>
    <div class="print-controls">
        <button class="mode-btn" id="modetoggle" onclick="toggleMode()">📋 Acknowledgement</button>
        <button class="print-btn" onclick="window.print()">🖨️ Print</button>
    </div>
    
    <div class="container">
        <div class="header">
            <div class="company-info">
                <div class="logo">CENTESE</div>
                <div class="company-details">
                    <div>Centese, Inc.</div>
                    <div>4156 S. 52nd Street</div>
                    <div>Omaha, NE 68117</div>
                    <div>US</div>
                    <div>(888)220-0040</div>
                    <div>orders@centese.com</div>
                </div>
            </div>
            
            <div class="document-info">
                <div class="document-title" data-mode-text data-std-text="Sales Order" data-ack-text="Sales Order Acknowledgement">Sales Order</div>
                <div class="document-number">${data.number}</div>
                <div>${data.orderDate}</div>
            </div>
        </div>
        
        <div class="addresses">
            <div class="address-section">
                <h3>Bill To</h3>
                <div><strong>${data.customer.billing.addressee}</strong></div>
                <div style="display:${
                  !!data.customer.billing.attn ? "inherit" : "none"
                }">${data.customer.billing.attn}</div>
                <div>${data.customer.billing.line1}</div>
                <div style="display:${
                  data.customer.billing.line2 ? "inherit" : "none"
                }">${data.customer.billing.line2}</div>
                <div style="display:${
                  data.customer.billing.line3 ? "inherit" : "none"
                }">${data.customer.billing.line3}</div>
                <div>${data.customer.billing.city} ${
                  data.customer.billing.state
                } ${data.customer.billing.zip}</div>
                <div>${data.customer.billing.country}</div>
            </div>
            
            <div class="address-section">
                <h3>Ship To</h3>
                <div><strong>${data.customer.shipping.addressee}</strong></div>
                <div style="display:${
                  !!data.customer.shipping.attn ? "inherit" : "none"
                }">${data.customer.shipping.attn}</div>
                <div>${data.customer.shipping.line1}</div>
                <div style="display:${
                  data.customer.shipping.line2 ? "inherit" : "none"
                }">${data.customer.shipping.line2}</div>
                <div style="display:${
                  data.customer.shipping.line3 ? "inherit" : "none"
                }">${data.customer.shipping.line3}</div>
                <div>${data.customer.shipping.city} ${
                  data.customer.shipping.state
                } ${data.customer.shipping.zip}</div>
                <div>${data.customer.shipping.country}</div>
            </div>
        </div>

        <table class="details-table">
          <thead>
            <tr>
              <th>Customer PO</th>
              <th data-mode-text data-std-text="Requested Ship Date" data-ack-text="Estimated Delivery Date">Requested Ship Date</th>
              <th>Shipping Method</th>
              <th>Terms</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${data.custPO}</td>
              <td data-mode-text data-std-text=${
                data.orderDate
              } data-ack-text=${data.requestDate}>${
                data.orderDate || "N/A"
              }</td>
              <td>${data.shipMethod}</td>
              <td>${data.terms}</td>
            </tr>
          </tbody>
        </table>
        
        <table class="items-table">
            <thead>
                <tr>
                    <th>Item</th>
                    <th class="quantity">Quantity</th>
                    <th class="uom">UoM</th>
                    <th class="unit-price">Unit Price</th>
                    <th class="extended-price">Extended Price</th>
                </tr>
            </thead>
            <tbody>
                ${data.items
                  .map(
                    (item) => `
                    <tr>
                        <td>
                            <div class="item-code">${item.number}</div>
                            <div class="item-description">${item.name}</div>
                        </td>
                        <td class="quantity">${item.quantity}</td>
                        <td class="uom">${item.uom}</td>
                        <td class="unit-price">$${item.rate}${
                          item.eval ? " (EVAL)" : ""
                        }</td>
                        <td class="extended-price">$${item.total}${
                          item.eval ? " (EVAL)" : ""
                        }</td>
                    </tr>
                `,
                  )
                  .join("")}
            </tbody>
        </table>
        
        <div class="totals">
            <table>
                <tr>
                    <td class="label">Subtotal</td>
                    <td class="amount">$${data.pricing.sub}</td>
                </tr>
                <tr>
                    <td class="label">Tax (0%)</td>
                    <td class="amount">$0.00</td>
                </tr>
                <tr>
                    <td class="label">S&H</td>
                    <td class="amount">TBD</td>
                </tr>
                <tr class="total-row">
                    <td class="label">Total</td>
                    <td class="amount">$${data.pricing.total}</td>
                </tr>
            </table>
        </div>
    </div>
    <script>
      let currentMode = 'std';

      function toggleMode() {
        currentMode = currentMode === 'std' ? 'ack' : 'std';
        updateContent();
        updateButton()
      }

      function updateContent() {
        const elements = document.querySelectorAll('[data-mode-text]');
        elements.forEach(element => {
          const stdText = element.getAttribute('data-std-text') || '';
          const ackText = element.getAttribute('data-ack-text') || '';
          const displayText = currentMode === 'std' ? stdText : ackText;

          element.textContent = displayText

          if(displayText.trim() === '' || !displayText){
            element.hidden = true
          } else {
            element.hidden = false
          }
        })
      }

      function updateButton() {
        const button = document.getElementById('modetoggle')
        if(currentMode === 'std'){
          button.textContent = '📋 Acknowledgement';
          button.title = 'Switch to Acknowledgement Mode';
        } else {
          button.textContent = '📄 Standard';
          button.title = 'Switch to Standard Mode';
        }
      }
        
      function updateTitle() {
          document.title = currentMode === 'std' ? 'SO ${data.number}' : 'SOA ${
            data.number
          }';
          
      }

      document.addEventListener('DOMContentLoaded', function() {
        updateContent();
        updateButton();
        updateTitle();
      })
    </script>
</body>
</html>`;
};

const FulfillmentDialog: React.FC<{
  open: any;
  control: Control<SOFormData, any, SOFormData>;
  onClose: () => void;
  index: number;
  item: ItemOption;
  quantNeeded: number;
  orderedUnit: UOMOption;
  lineComplete: boolean;
}> = ({
  open,
  control,
  onClose,
  index,
  item,
  quantNeeded,
  orderedUnit,
  lineComplete,
}) => {
  const [currFillData, setCurrFillData] = useState<{
    quantity: number;
    position: PositionOption | null;
  }>({
    quantity: 0,
    position: null,
  });

  const {
    fields: fillFields,
    append: fillAppend,
    remove: fillRemove,
    replace: fillFieldsReplace,
  } = useFieldArray({
    control,
    name: `line_items.${index}.new_fills`,
  });

  const [error, setError] = useState<string | null>(null);
  const [quantPendingFill, setQuantPendingFill] = useState<number>(0);
  const quantRemaining = useMemo(() => {
    return Math.max(quantNeeded - quantPendingFill, 0);
  }, [quantPendingFill, quantNeeded]);

  const canAdd = useMemo(() => {
    return (
      currFillData.quantity &&
      currFillData.quantity > 0 &&
      !!currFillData.position
    );
  }, [currFillData]);

  const addLine = () => {
    fillAppend({ ...currFillData, uom: orderedUnit } as NewFillData);
    setCurrFillData({
      quantity: 0,
      position: null,
    });
  };

  const handleAdd = () => {
    if (canAdd) {
      addLine();
    }
  };

  const handleRemove = (ind: number) => {
    fillRemove(ind);
  };

  const handleQuantChange = (newValue: number) => {
    setCurrFillData((prev) => ({
      ...prev,
      quantity: newValue,
    }));
  };

  const handlePosChange = (newValue: PositionOption | null) => {
    console.log("New Pos", newValue);
    setCurrFillData((prev) => ({
      ...prev,
      position: newValue,
    }));
  };

  const {
    data: posData,
    error: posError,
    isLoading: posLoading,
  } = useTSQInvPositionFulfillment(item?.value.id || "", !!item);
  const posOpts: PositionOption[] = useMemo(() => {
    if (posData?.items) {
      return posData.items.map((val) => ({
        label: `${val.inventory_lot.lot_number} - ${val.location.name} - ${val.status.name}`,
        type: "inv_pos",
        value: {
          id: val.id,
          lot: val.inventory_lot.lot_number,
          lotId: val.inventory_lot.id,
          lotExpiration: val.inventory_lot.expiration_date || null,
          quantity: parseFloat(val.quantity_on_hand),
        },
      }));
    } else return [];
  }, [posData]);

  useEffect(() => {
    if (open) {
      let temp = 0;
      for (let f of fillFields) {
        const val: NewFillData = f as any;
        temp += val.quantity;
      }
      setQuantPendingFill(temp);
    } else {
      setQuantPendingFill(0);
    }
  }, [open, fillFields]);

  const handleClose = (event: any, reason: string | null) => {
    if (reason === "backdropClick") {
      console.log("DIALOG CLOSE VIA BACKDROP CLICK");
    } else {
      console.log("DIALOG CLOSE VIA OTHER METHOD", event, reason);
    }
    setCurrFillData({
      quantity: 0,
      position: null,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Fulfillment</DialogTitle>
      <DialogContent>
        <Stack direction="column" width="100%" gap={2} p={5}>
          {lineComplete && (
            <Alert severity="warning">
              Line is already completed, are you sure you want to fulfill it
              more?
            </Alert>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography variant="body2">
              Quantity Remaining to Fulfill:
            </Typography>
            <Typography variant="body2">{`${
              quantNeeded || 0 - quantPendingFill
            }x ${orderedUnit?.label || ""}`}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography variant="body2">
              Quantity Pending Fulfillment:
            </Typography>
            <Typography variant="body2">{`${quantPendingFill}x ${
              orderedUnit?.label || ""
            }`}</Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "auto auto auto auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <Autocomplete
              sx={{ gridColumnStart: 1, gridColumnEnd: 3, gridRow: 1 }}
              fullWidth
              value={currFillData.position}
              options={posOpts}
              onChange={(event, newValue: PositionOption | null) => {
                handlePosChange(newValue);
              }}
              getOptionLabel={(option: PositionOption) => option.label}
              renderInput={(params) => (
                <TextField {...params} label="From Lot" />
              )}
            />
            <Box sx={{ gridColumn: 1, gridRow: 2 }}>
              <NumericEntryField
                value={currFillData.quantity}
                onChange={handleQuantChange}
                decimalPlaces={4}
                isCurrency={false}
                disabled={!currFillData.position}
                endAdornment={orderedUnit?.label || ""}
                textFieldProps={{ fullWidth: true }}
              />
            </Box>
            <Box display="flex" sx={{ gridColumn: 2, gridRow: 2 }}>
              <Typography variant="body2">{`Quantity Available: ${
                (currFillData.position?.value.quantity || 0) /
                orderedUnit.value.conversion
              }x ${orderedUnit.label}`}</Typography>
            </Box>
            <Button
              sx={{ gridColumn: 2, gridRow: 3 }}
              variant="outlined"
              color="primary"
              onClick={handleAdd}
              disabled={!canAdd}
              startIcon={<AddIcon />}
            >
              Confirm
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="5%">Row #</TableCell>
                  <TableCell width="auto">From Lot</TableCell>
                  <TableCell width="auto">Quantity</TableCell>
                  <TableCell width="auto">Expiration</TableCell>
                  <TableCell width="20%">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fillFields.map((newfill, newFillInd) => {
                  const value: NewFillData = newfill as any;
                  return (
                    <TableRow key={newfill.id}>
                      <TableCell>
                        <Typography variant="body1">
                          #{newFillInd + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack
                          sx={{
                            direction: "column",
                            gap: 1,
                          }}
                        >
                          <Typography variant="body1">
                            {value.position?.label}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack
                          sx={{
                            direction: "column",
                            gap: 1,
                          }}
                        >
                          <Typography variant="body1">
                            {value.quantity}x
                          </Typography>
                          <Typography variant="body1">
                            {orderedUnit?.label}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {value.position?.value.lotExpiration
                            ? value.position?.value.lotExpiration
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            handleRemove(newFillInd);
                          }}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={(e) => handleClose(e, "Close Button Press")}
        >
          Close Dialog
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SOLine: React.FC<{
  index: number;
  fieldData: FieldArrayWithId<SOFormData, "line_items", "id">;
  control: Control<SOFormData, any, SOFormData>;
  setValue: any;
  allItems: ItemOption[];
  allUoms: UOMOption[];
  errors: any;
  formMode: string;
  remove: (index: number) => void;
}> = ({
  index,
  fieldData,
  control,
  setValue,
  allItems = [],
  allUoms = [],
  formMode,
  errors,
  remove,
}) => {
  const [prevFillsOpen, setPrevFillsOpen] = useState<boolean>(false);
  const [filteredUOMOpts, setFilteredUOMOpts] = useState<UOMOption[]>(
    allUoms.filter(
      (uomOpt) =>
        uomOpt.value.typeId ===
        (fieldData.item ? fieldData.item.value.uomTypeId : null),
    ),
  );
  const [isDelete, setIsDelete] = useState<boolean>(fieldData.is_delete);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const {
    fields: existFills,
    append: existFillAppend,
    remove: existFillRemove,
    replace: existFillReplace,
  } = useFieldArray({
    control,
    name: `line_items.${index}.fulfillments`,
  });

  const canEditLine: boolean = useMemo(() => {
    if (formMode === "create") {
      return true;
    } else if (formMode === "view" || formMode === "fulfill" || isDelete) {
      return false;
    } else if (formMode === "edit") {
      if (!!fieldData.quantity_fulfilled && fieldData.quantity_fulfilled > 0) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }, [fieldData, formMode, isDelete]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleRemove = () => {
    if (fieldData.is_new) {
      remove(index);
    } else {
      setValue(`line_items.${index}.is_delete`, !isDelete);
      setIsDelete(!isDelete);
    }
  };

  const handleItemChange = (newValue: ItemOption | null) => {
    setFilteredUOMOpts(
      allUoms.filter(
        (uomOpt) =>
          uomOpt.value.typeId === (newValue ? newValue.value.uomTypeId : null),
      ),
    );
    const uomOpt =
      allUoms.find((u) => u.value.id === newValue?.value.sales_uom) || null;
    setValue(`line_items.${index}.uom_unit`, uomOpt);
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Typography variant="body2" sx={{ textWrap: "nowrap" }}>
            {`#${index + 1}`}
            {isDelete ? <br /> : <></>}
            {isDelete ? "(REMOVED)" : ""}
          </Typography>
        </TableCell>
        <TableCell>
          <Stack sx={{ gap: 2 }}>
            <Controller
              name={`line_items.${index}.item`}
              control={control}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { error },
              }) => (
                <Autocomplete
                  fullWidth
                  {...restField}
                  disabled={!canEditLine}
                  value={value}
                  options={allItems}
                  onChange={(event, newValue) => {
                    handleItemChange(newValue);
                    onChange(newValue);
                  }}
                  getOptionLabel={(option: ItemOption) => option.label}
                  isOptionEqualToValue={(
                    option: ItemOption,
                    value: ItemOption,
                  ) => option.value.id === value.value.id}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Item" />
                  )}
                />
              )}
            />
            <Controller
              name={`line_items.${index}.uom_unit`}
              control={control}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { error },
              }) => (
                <Autocomplete
                  fullWidth
                  {...restField}
                  disabled={!canEditLine}
                  value={value}
                  options={filteredUOMOpts}
                  onChange={(event, newValue) => {
                    onChange(newValue);
                  }}
                  getOptionLabel={(option: UOMOption) => option.label}
                  isOptionEqualToValue={(option: UOMOption, value: UOMOption) =>
                    option.value.id === value.value.id
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Unit" />
                  )}
                />
              )}
            />
          </Stack>
        </TableCell>
        <TableCell>
          <Stack sx={{ gap: 2 }}>
            <Controller
              name={`line_items.${index}.quantity_ordered`}
              control={control}
              rules={{ required: "Must Enter Ordered Quantity" }}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { error },
              }) => (
                <NumericEntryField
                  value={value || 0}
                  onChange={onChange}
                  min={0}
                  decimalPlaces={4}
                  label="Ordered"
                  isCurrency={false}
                  disabled={!canEditLine}
                  textFieldProps={{ size: "small", ...restField }}
                />
              )}
            />
            {!(formMode === "create") && (
              <Controller
                name={`line_items.${index}.quantity_fulfilled`}
                control={control}
                render={({
                  field: { value, onChange, ...restField },
                  fieldState: { error },
                }) => (
                  <NumericEntryField
                    value={value || 0}
                    onChange={onChange}
                    min={0}
                    decimalPlaces={4}
                    label="Fulfilled"
                    disabled={true}
                    isCurrency={false}
                    endAdornment={
                      <InputAdornment position="end">
                        <Tooltip
                          title={
                            fieldData.line_completed
                              ? "Line Complete"
                              : "Line Pending Fulfillment"
                          }
                        >
                          {fieldData.line_completed ? <CheckIcon /> : <></>}
                        </Tooltip>
                      </InputAdornment>
                    }
                    textFieldProps={{ size: "small", ...restField }}
                  />
                )}
              />
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Controller
            name={`line_items.${index}.unit_cost`}
            control={control}
            rules={{ required: "Must Enter Unit Cost" }}
            render={({
              field: { value, onChange, ...restField },
              fieldState: { error },
            }) => (
              <NumericEntryField
                value={value || 0}
                onChange={onChange}
                min={0}
                decimalPlaces={4}
                label="Unit Cost"
                isCurrency={true}
                disabled={!canEditLine}
                textFieldProps={{ size: "small", ...restField }}
              />
            )}
          />
        </TableCell>
        <TableCell>
          <Controller
            name={`line_items.${index}.notes`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type="text"
                size="medium"
                error={!!error}
                disabled={!canEditLine}
              />
            )}
          />
        </TableCell>
        <TableCell>
          <Stack direction="column">
            {formMode === "fulfill" && fieldData.requires_fulfillment ? (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleDialogOpen}
                size="small"
              >
                Fulfill Line
              </Button>
            ) : formMode === "create" || formMode === "edit" ? (
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleRemove()}
                size="small"
                startIcon={<DeleteIcon />}
                disabled={!canEditLine}
              >
                Remove
              </Button>
            ) : (
              <></>
            )}
            <Controller
              name={`line_items.${index}.eval_line`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={!canEditLine}
                    />
                  }
                  label="Eval?"
                />
              )}
            />
            <Controller
              name={`line_items.${index}.requires_fulfillment`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={!canEditLine}
                    />
                  }
                  label="Fill?"
                />
              )}
            />
          </Stack>
        </TableCell>
      </TableRow>
      {formMode === "fulfill" && fieldData.requires_fulfillment && (
        <FulfillmentDialog
          open={dialogOpen}
          control={control}
          onClose={handleDialogClose}
          index={index}
          quantNeeded={
            (fieldData.quantity_ordered || 0) -
            (fieldData.quantity_fulfilled || 0)
          }
          item={fieldData.item!}
          orderedUnit={fieldData.uom_unit!}
          lineComplete={fieldData.line_completed}
        />
      )}
    </>
  );
};

export const PageSalesOrderForm: React.FC = () => {
  const location = useLocation();

  // Mode detection from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const formMode = searchParams.get("mode") || "";
  const refFormMode = useRef(formMode);

  const [recId, setRecId] = useState<string | null>(id);

  const today = new Date();
  const tomorrow = (() => {
    let temp = new Date(today);
    temp.setDate(temp.getDate() + 1);
    return temp;
  })();

  const formDefaults = {
    number: null,
    order_date: today.toISOString(),
    requested_date: tomorrow.toISOString(),
    customer_po: null,
    rush_order: false,
    order_status: "",
    approval_status: "",
    fulfillment_status: "",
    tax_rate: 0,
    ship_cost: 0,
    notes: null,
    order_terms: null,
    customer: null,
    location: null,
    ship_billing: null,
    ship_method: null,
    bill_addr: null,
    ship_addr: null,
    sales_rep: null,
    customer_contact: null,
    line_items: [],
    create_shipment: true,
  };
  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<SOFormData>({
    resolver: yupResolver(salesOrderSchema) as any,
    defaultValues: formDefaults,
  });

  const worldLocContext = useWorldLocationData();

  const refRenderCount = useRef<number>(0);
  const refOrigData = useRef<SalesOrderFull | null>(null);
  const [navToView, setNavToView] = useState<boolean>(false);
  const [navToList, setNavToList] = useState<boolean>(false);
  const [displayProcessDialog, setDisplayProcessDialog] =
    useState<boolean>(false);

  const handleProcessDialogClose = useCallback(
    (event: any, reason: "backdropClick" | "escapeKeyDown") => {
      if (reason === "backdropClick") {
        return;
      }
      setDisplayProcessDialog(false);
      if (navToView && recId) {
        window.open(`/orders/sales/form?mode=view&id=${recId}`, "_self");
      } else if (navToList) {
        window.open("/orders/sales", "_self");
      }
    },
    [navToView, navToList, recId],
  );

  const [overrideSOGen, setOverrideSOGen] = useState<boolean>(false);
  const [origSONum, setOrigSONum] = useState<string | null>(null);
  const [billAddrEdits, setBillAddrEdits] = useState<OrderAddressEdit>({
    addressee: null,
    attn: null,
    line_1: null,
    line_2: null,
    line_3: null,
    city: null,
    state: null,
    country: null,
    zip: null,
    phone: null,
    email: null,
  });
  const [shipAddrEdits, setShipAddrEdits] = useState<OrderAddressEdit>({
    addressee: null,
    attn: null,
    line_1: null,
    line_2: null,
    line_3: null,
    city: null,
    state: null,
    country: null,
    zip: null,
    phone: null,
    email: null,
  });

  // Form State
  const [error, setError] = useState<string | null>(null);

  const [selectedCustId, setSelectedCustId] = useState<string | null>(null);

  const [formResetting, setFormResetting] = useState<boolean>(false);

  const createSOMutation = useTSMCreateSalesOrder();
  const postProcSOMutation = useTSMPostProcessSalesOrder();
  const updateSOMutation = useTSMUpdateSalesOrder();
  const fulfillSOMutation = useTSMFulfillSalesOrder();
  const completeSOMutation = useTSMCompleteSO();
  const voidSOMutation = useTSMVoidSO();
  const statUpdateMutation = useTSMStatusUpdateSO();
  const checkDupeMutation = useTSMCheckForDupePO();

  const {
    data: thisSOData,
    isLoading: thisSOLoading,
    error: thisSOError,
    refetch: thisSORefetch,
    invalidate: thisSOInvalidate,
  } = useTSQSalesOrder(
    recId,
    (formMode === "view" || formMode === "edit" || formMode === "fulfill") &&
      !formResetting,
  );

  const {
    data: itemsResponse,
    isLoading: itemsLoading,
    error: itemsError,
  } = useTSQItemList(
    {
      limit: -1,
      active_status: "active",
      filters: [[{ field: "catalog_name", operator: "!=", value: null }]],
    },
    true,
  );
  const itemOpts: ItemOption[] = useMemo(() => {
    if (itemsResponse?.items) {
      return itemsResponse.items.map((val) => ({
        label: val.catalog_name || "UNKNOWN CATALOG ITEM",
        type: "item",
        value: {
          id: val.id,
          number: val.number,
          name: val.name,
          uomTypeId: val.uom_type.id,
          sales_uom: val.sales_uom.id,
        },
      }));
    } else {
      return [];
    }
  }, [itemsResponse]);

  const {
    data: unitsResponse,
    isLoading: unitsLoading,
    error: unitsError,
  } = useTSQUOMUnitList({
    limit: -1,
    active_status: "active",
    filters: [[]],
  });
  const uomOpts: UOMOption[] = useMemo(() => {
    if (unitsResponse?.items) {
      return unitsResponse.items.map((val) => ({
        label: val.unit_symbol,
        type: "unit",
        value: {
          id: val.id,
          symbol: val.unit_symbol,
          conversion: parseFloat(val.conversion_factor),
          typeId: val.uom_type_id,
        },
      }));
    } else {
      return [];
    }
  }, [unitsResponse]);

  const {
    data: locsResponse,
    isLoading: locsLoading,
    error: locsError,
  } = useTSQLocationList({
    limit: -1,
    active_status: "active",
    filters: [[]],
  });
  const locOpts: LocationOption[] = useMemo(() => {
    if (locsResponse?.items) {
      return locsResponse.items
        .filter((loc) => {
          return loc.parent_location === null;
        })
        .map((val) => ({
          label: `${val.name}`,
          type: "location",
          value: { id: val.id },
        }));
    } else {
      return [];
    }
  }, [locsResponse]);
  const omaLoc: LocationOption | null = (() =>
    locOpts.find((opt) => opt.label === "Omaha") || null)();

  const {
    data: customerResponse,
    isLoading: customerLoading,
    error: customerError,
  } = useTSQCompanyList(
    {
      limit: -1,
      skip: -1,
      active_status: "active",
      filters: [[]],
    },
    "customer",
    true,
  );
  const custOpts: CustomerOption[] = useMemo(() => {
    if (customerResponse?.items) {
      return customerResponse.items.map((val) => ({
        label: `${val.code} - ${val.name}`,
        type: "customer",
        value: {
          id: val.id,
          contact_id:
            val.contacts.find((cont) => cont.contact_type === "PRIMARY")?.id ||
            null,
          bill_id: val.primary_billing_addr_id || null,
          ship_id: val.primary_shipping_addr_id || null,
          rep_id: null,
          terms: val.default_payment_terms,
          ship_bill: (val as CustomerFull).shipment_billing_preference || null,
          ship_provider: val.preferred_shipping_provider || null,
          ship_method: val.preferred_shipping_methods || null,
        },
      }));
    } else {
      return [];
    }
  }, [customerResponse]);

  const {
    data: salesRepsResponse,
    isLoading: salesRepsLoading,
    error: salesRepsError,
  } = useTSQPeopleList(
    {
      skip: -1,
      limit: -1,
      active_status: "active",
      filters: [[{ field: "sales_rep", operator: "==", value: true }]],
    },
    "employee",
  );
  const salesRepOpts: SalesRepOption[] = useMemo(() => {
    if (salesRepsResponse?.items) {
      return salesRepsResponse.items.map((val) => ({
        label: `${val.first_name} ${val.last_name}`,
        type: "sales_rep",
        value: { id: val.id, name: `${val.first_name} ${val.last_name}` },
      }));
    } else {
      return [];
    }
  }, [salesRepsResponse]);

  const baseDataLoading =
    itemsLoading ||
    unitsLoading ||
    customerLoading ||
    locsLoading ||
    salesRepsLoading;

  const dataReady =
    !baseDataLoading &&
    itemOpts.length > 0 &&
    uomOpts.length > 0 &&
    custOpts.length > 0 &&
    locOpts.length > 0 &&
    salesRepOpts.length > 0;

  const {
    data: addrResponse,
    isLoading: addrLoading,
    error: addrError,
  } = useTSQAddressList(
    {
      skip: -1,
      limit: -1,
      active_status: "active",
      filters: [
        [
          {
            field: "entity_id",
            operator: "==",
            value: selectedCustId,
          },
        ],
      ],
    },
    !!selectedCustId,
  );
  const allAddrOpts: AAddressOption[] = useMemo(() => {
    if (addrResponse?.items) {
      return addrResponse.items.map((val) => ({
        label: val.name,
        type: "address",
        value: { id: val.id, addr_type: val.address_type },
      }));
    } else {
      return [];
    }
  }, [addrResponse]);
  const availBillAddrOpts: AAddressOption[] = useMemo(() => {
    return allAddrOpts.filter((opt) => opt.value.addr_type !== "SHIPPING");
  }, [allAddrOpts]);
  const availShipAddrOpts: AAddressOption[] = useMemo(() => {
    return allAddrOpts.filter((opt) => opt.value.addr_type !== "BILLING");
  }, [allAddrOpts]);

  const {
    data: contactsResponse,
    isLoading: contactsLoading,
    error: contactsError,
  } = useTSQPeopleList(
    {
      skip: -1,
      limit: -1,
      active_status: "active",
      filters: [
        [
          {
            field: "company_id",
            operator: "==",
            value: selectedCustId,
          },
        ],
      ],
    },
    "contact",
    !!selectedCustId,
  );
  const contactOpts: ContactOption[] = useMemo(() => {
    if (contactsResponse?.items) {
      return contactsResponse.items.map((val) => ({
        label: `${val.first_name} ${val.last_name}`,
        type: "contact",
        value: { id: val.id },
      }));
    } else {
      return [];
    }
  }, [contactsResponse]);

  const custInfoLoading = addrLoading || contactsLoading;

  // Put the cursor in the Customer field when a new sales order is opened
  // (J. Whybark). Runs once per new order, so it does not jump back to
  // Customer after one is picked and their addresses load.
  const customerInputRef = useRef<HTMLInputElement | null>(null);
  const refCustomerAutoFocused = useRef(false);
  useEffect(() => {
    if (formMode !== "create") {
      refCustomerAutoFocused.current = false;
      return;
    }
    if (
      !refCustomerAutoFocused.current &&
      !custInfoLoading &&
      customerInputRef.current
    ) {
      customerInputRef.current.focus();
      refCustomerAutoFocused.current = true;
    }
  });
  const addrReady = !addrLoading && allAddrOpts.length > 0;

  const [formInitState, setFormInitState] = useState<boolean>(false);
  const refInitState = useRef<boolean>(formInitState);

  const {
    fields: soLineItems,
    append: appendSOLine,
    remove: removeSOLine,
  } = useFieldArray({
    control,
    name: "line_items",
  });

  const [
    watchedLocOpt,
    watchedCustOpt,
    watchedBillAddrOpt,
    watchedShipAddrOpt,
    isRushOrder,
    watchedSONum,
  ] = watch([
    "location",
    "customer",
    "bill_addr",
    "ship_addr",
    "rush_order",
    "number",
  ]);

  const [dupeCustPO, setDupeCustPO] = useState<string | null>(null);
  const handleDupeCheck = async (cust_po: string) => {
    const dupeCheck = await checkDupeMutation.mutateAsync({
      cust_po: cust_po,
      exclude_so: !!watchedSONum ? [watchedSONum] : [],
    });
    if (!dupeCheck.success) {
      setDupeCustPO(dupeCheck.message);
    }
    return;
  };

  const watchedSOLines = useWatch({
    control,
    name: "line_items",
  });
  const { lineOrdQuants, lineOrdCosts, lineCount } = useMemo(() => {
    const retVal: {
      lineOrdQuants: number[];
      lineOrdCosts: number[];
      lineCount: number;
    } = {
      lineOrdQuants: [],
      lineOrdCosts: [],
      lineCount: watchedSOLines.length,
    };
    watchedSOLines.forEach((line) => {
      retVal.lineOrdQuants.push(line.quantity_ordered || 0);
      retVal.lineOrdCosts.push(line.eval_line ? 0 : line.unit_cost || 0);
    });
    return retVal;
  }, [watchedSOLines]);

  const initFormWithData = useCallback(
    (orderData: SalesOrderFull) => {
      refOrigData.current = orderData;
      setOrigSONum(orderData.number);
      console.log("PROVIDED ORDER DATA", orderData);
      const termOpt =
        paymentTermOptions.find(
          (opt) => opt.value.id === orderData.order_terms,
        ) || null;
      const custOpt =
        custOpts.find((opt) => opt.value.id === orderData.customer.id) || null;
      const locOpt =
        locOpts.find((opt) => opt.value.id === orderData.location.id) || null;
      const shipBillOpt =
        customerShipmentBillingOptions.find(
          (opt) => opt.value.id === orderData.selected_shipment_billing,
        ) || null;
      const shipMethodOpt =
        shippingMethodOptions.find(
          (opt) => opt.value.id === orderData.selected_shipping_method,
        ) || null;
      const billAddrOpt =
        allAddrOpts.find(
          (opt) => opt.value.id === orderData.selected_billing_addr.id,
        ) || null;
      setBillAddrEdits(orderData.selected_billing_addr_edits);
      const shipAddrOpt =
        allAddrOpts.find(
          (opt) => opt.value.id === orderData.selected_shipping_addr.id,
        ) || null;
      setShipAddrEdits(orderData.selected_shipping_addr_edits);
      const repOpt =
        salesRepOpts.find((opt) => opt.value.id === orderData.sales_rep?.id) ||
        null;
      const contactOpt =
        contactOpts.find(
          (opt) => opt.value.id === orderData.customer_contact?.id,
        ) || null;
      const orderLines: SOLineData[] = orderData.line_items.map((orderLine) => {
        const itemOpt =
          itemOpts.find((opt) => opt.value.id === orderLine.item.id) || null;
        const uomOpt =
          uomOpts.find((opt) => opt.value.id === orderLine.uom_unit.id) || null;
        return {
          id: orderLine.id,
          item: itemOpt,
          uom_unit: uomOpt,
          quantity_ordered: parseFloat(orderLine.quantity_ordered),
          quantity_fulfilled: parseFloat(orderLine.quantity_fulfilled),
          unit_cost: parseFloat(orderLine.unit_cost),
          notes: orderLine.notes || null,
          is_delete: false,
          is_new: false,
          requires_fulfillment: orderLine.requires_fulfillment,
          eval_line: orderLine.eval_line,
          mark_complete: false,
          line_completed: orderLine.line_completed,
          fulfillments: [],
          new_fills: [],
        };
      });
      reset({
        number: orderData.number,
        order_date: orderData.order_date,
        requested_date: orderData.requested_date || null,
        customer_po: orderData.customer_po,
        rush_order: orderData.rush_order,
        order_status: orderData.order_status,
        approval_status: orderData.approval_status,
        fulfillment_status: orderData.fulfillment_status,
        tax_rate: parseFloat(orderData.tax_rate),
        ship_cost: parseFloat(orderData.ship_cost),
        notes: orderData.notes || null,
        order_terms: termOpt,
        customer: custOpt,
        location: locOpt,
        ship_billing: shipBillOpt,
        ship_method: shipMethodOpt,
        bill_addr: billAddrOpt,
        ship_addr: shipAddrOpt,
        sales_rep: repOpt,
        customer_contact: contactOpt,
        line_items: orderLines,
        create_shipment: true,
      });
      refOrigData.current = orderData;
      setFormInitState(true);
    },
    [
      allAddrOpts,
      contactOpts,
      locOpts,
      itemOpts,
      custOpts,
      reset,
      salesRepOpts,
      uomOpts,
    ],
  );

  useEffect(() => {
    console.log("Render #", refRenderCount.current);
    console.log("Base Data Ready", dataReady);
    console.log("Cust Info Data Loading", custInfoLoading);
    console.log("Form Init State", refInitState.current);
    console.log("Form Mode", formMode);
    console.log("Ref Form Mode", refFormMode.current);
  });

  useEffect(() => {
    if (displayProcessDialog) {
      if (formMode === "create") {
        if (!createSOMutation.isPending && !postProcSOMutation.isPending) {
          setNavToView(createSOMutation.isSuccess);
          setTimeout(() => {
            handleProcessDialogClose(null, "escapeKeyDown");
          }, 5000);
        }
      }
      if (formMode === "fulfill") {
        if (!fulfillSOMutation.isPending) {
          setNavToList(fulfillSOMutation.isSuccess);
          setTimeout(() => {
            handleProcessDialogClose(null, "escapeKeyDown");
          }, 5000);
        }
      }
      if (formMode === "edit") {
        if (!updateSOMutation.isPending) {
          setNavToView(updateSOMutation.isSuccess);
          setTimeout(() => {
            handleProcessDialogClose(null, "escapeKeyDown");
          }, 5000);
        }
      }
    }
  }, [
    updateSOMutation,
    createSOMutation,
    postProcSOMutation,
    fulfillSOMutation,
    formMode,
    displayProcessDialog,
    handleProcessDialogClose,
  ]);

  useEffect(() => {
    console.log(`DA BIG EFFECT RENDER #${refRenderCount.current}`);
    if (formResetting) {
      if (formMode === refFormMode.current) {
        console.log(
          "DA BIG EFFECT FORM RESETTING, AWAITING UPDATED FORM MODE",
          {
            render: refRenderCount.current,
            formMode: formMode,
            refFormMode: refFormMode.current,
          },
        );
        return;
      } else {
        console.log(
          "DA BIG EFFECT FORM RESETTING, RECEIVED UPDATED FORM MODE, ENDING FORMRESET",
          {
            render: refRenderCount.current,
            formMode: formMode,
            refFormMode: refFormMode.current,
          },
        );
        refFormMode.current = formMode;
        setFormResetting(false);
      }
    }
    if (dataReady) {
      console.log("DA BIG EFFECT DATA READY", {
        render: refRenderCount.current,
        formMode: formMode,
      });
      if (formMode === "create") {
        console.log("DA BIG EFFECT FORM CREATE", {
          render: refRenderCount.current,
          formMode: formMode,
          initComplete: refInitState.current,
          omaLoc: omaLoc,
        });
        if (!refInitState.current && !!omaLoc) {
          setValue("location", omaLoc);
          setFormInitState(true);
        }
      } else {
        console.log("DA BIG EFFECT NOT CREATE", {
          render: refRenderCount.current,
          formMode: formMode,
          soLoading: thisSOLoading,
          soData: thisSOData,
          initComplete: refInitState.current,
          // itemOpts: itemOpts,
          // uomOpts: uomOpts,
          // custOpts: custOpts,
          // locOpts: locOpts,
          // salesRepOpts: salesRepOpts,
          selectedCustId: selectedCustId,
        });
        if (thisSOLoading) {
          console.log("DA BIG EFFECT SO LOADING", {
            render: refRenderCount.current,
          });
          return;
        } else {
          if (thisSOData) {
            console.log("DA BIG EFFECT SO DATA", {
              render: refRenderCount.current,
            });
            if (!selectedCustId) {
              console.log("DA BIG EFFECT NO CUST ID", {
                render: refRenderCount.current,
              });
              setSelectedCustId(thisSOData.customer.id);
            } else {
              console.log("DA BIG EFFECT WITH CUST ID", {
                render: refRenderCount.current,
                selectedCustId: selectedCustId,
              });
              if (!refInitState.current && addrReady) {
                initFormWithData(thisSOData);
              }
            }
          } else {
            setError("Error loading Sales Order Data!");
          }
        }
      }
    }
  }, [
    dataReady,
    formMode,
    setValue,
    thisSOLoading,
    thisSOData,
    omaLoc,
    // itemOpts,
    // uomOpts,
    // custOpts,
    // locOpts,
    // salesRepOpts,
    addrReady,
    selectedCustId,
    initFormWithData,
    formResetting,
  ]);

  useEffect(() => {
    console.log("CUSTOMER DATA USEEFFECT", {
      render: refRenderCount.current,
      formInitComplete: refInitState.current,
      allAddrOpts: allAddrOpts,
      contactOpts: contactOpts,
      watchedCustOpt: watchedCustOpt,
      selectedCustId: selectedCustId,
    });
    if (formResetting) {
      console.log(
        "CUSTOMER DATA USEEFFECT FORM RESETTING",
        refRenderCount.current,
      );
      return;
    }
    if (!refInitState.current) {
      console.log(
        "CUSTOMER DATA USEEFFECT FORM NOT INIT",
        refRenderCount.current,
      );
      return;
    } else {
      if (watchedCustOpt) {
        console.log("CUSTOMER DATA USEEFFECT WITH CUST OPT", {
          render: refRenderCount.current,
          custOpt: watchedCustOpt,
        });
        if (watchedCustOpt.value.id === selectedCustId) {
          console.log(
            "CUSTOMER DATA USEEFFECT WITH CUST OPT MATCH SELECTEDCUSTID",
            {
              render: refRenderCount.current,
              custOpt: watchedCustOpt,
              selectedCustId: selectedCustId,
            },
          );
          const billOpt =
            allAddrOpts.find(
              (opt) => opt.value.id === watchedCustOpt.value.bill_id,
            ) || null;
          setValue("bill_addr", billOpt);
          const shipOpt =
            allAddrOpts.find(
              (opt) => opt.value.id === watchedCustOpt.value.ship_id,
            ) || null;
          setValue("ship_addr", shipOpt);
          const contactOpt =
            contactOpts.find(
              (opt) => opt.value.id === watchedCustOpt.value.contact_id,
            ) || null;
          setValue("customer_contact", contactOpt);
          const termOpt =
            paymentTermOptions.find(
              (opt) => opt.value.id === watchedCustOpt.value.terms,
            ) || null;
          setValue("order_terms", termOpt);
          const shipMethodOpt =
            shippingMethodOptions.find(
              (opt) =>
                opt.value.id ===
                `${watchedCustOpt.value.ship_provider} ${
                  watchedCustOpt.value.ship_method
                    ? watchedCustOpt.value.ship_method[
                        watchedCustOpt.value.ship_provider === "fedex"
                          ? "fedex"
                          : "ups"
                      ]
                    : ""
                }`,
            ) || null;
          setValue("ship_method", shipMethodOpt);
          const shipBillOpt =
            customerShipmentBillingOptions.find(
              (opt) => opt.value.id === watchedCustOpt.value.ship_bill,
            ) || null;
          setValue("ship_billing", shipBillOpt);
        } else {
          console.log(
            "CUSTOMER DATA USEEFFECT WITH CUST OPT MISMATCH SELECTEDCUSTID",
            {
              render: refRenderCount.current,
              custOpt: watchedCustOpt,
              selectedCustId: selectedCustId,
            },
          );
          setSelectedCustId(watchedCustOpt.value.id);
        }
      } else {
        setSelectedCustId(null);
      }
    }
  }, [
    watchedCustOpt,
    selectedCustId,
    allAddrOpts,
    contactOpts,
    setValue,
    formResetting,
  ]);

  useEffect(() => {
    refInitState.current = formInitState;
  }, [formInitState]);

  const subtotal = useMemo(() => {
    var tot = 0;
    for (let i = 0; i < lineCount; i++) {
      tot += lineOrdQuants[i] * lineOrdCosts[i];
    }
    return tot;
  }, [lineOrdQuants, lineOrdCosts, lineCount]);

  const rushTotal = useMemo(() => {
    if (isRushOrder) {
      return subtotal * 0.1;
    } else {
      return 0;
    }
  }, [subtotal, isRushOrder]);

  const grandTotal = useMemo(() => {
    return subtotal + rushTotal;
  }, [subtotal, rushTotal]);

  useEffect(() => {
    refRenderCount.current += 1;
  });

  // Form Functions
  // Mode switching functions
  const switchToViewMode = () => {
    window.open(`/orders/sales/form?mode=view&id=${recId}`, "_self");
  };

  const switchToEditMode = () => {
    window.open(`/orders/sales/form?mode=edit&id=${recId}`, "_self");
  };

  const switchToFulfillMode = () => {
    window.open(`/orders/sales/form?mode=fulfill&id=${recId}`, "_self");
  };

  const switchToCreateMode = () => {
    resetForm();
    window.open(`/orders/sales/form?mode=create`, "_self");
  };

  const resetForm = () => {
    setFormResetting(true);
    console.log("FORM RESETTING");
    reset(formDefaults);
    console.log("FORM RESET WITH DEFAULT VALUES");
    setBillAddrEdits({
      addressee: null,
      attn: null,
      line_1: null,
      line_2: null,
      line_3: null,
      city: null,
      state: null,
      country: null,
      zip: null,
      phone: null,
      email: null,
    });
    setShipAddrEdits({
      addressee: null,
      attn: null,
      line_1: null,
      line_2: null,
      line_3: null,
      city: null,
      state: null,
      country: null,
      zip: null,
      phone: null,
      email: null,
    });
    console.log("ADDR EDITS RESET");
    setSelectedCustId(null);
    console.log("SELECTEDCUSTID RESET");
    setOrigSONum(null);
    setOverrideSOGen(false);
    refOrigData.current = null;
    console.log("OVERRIDE, ORIGSONUM, AND REFORIGDATA RESET");
    setFormInitState(false);
    console.log("FORMINITSTATE RESET");
    // thisSOInvalidate()
  };

  const addSOLine = () => {
    appendSOLine({
      id: null,
      item: null,
      uom_unit: null,
      quantity_ordered: 0,
      quantity_fulfilled: 0,
      unit_cost: 0,
      notes: "",
      requires_fulfillment: true,
      eval_line: false,
      is_delete: false,
      is_new: true,
      mark_complete: false,
      line_completed: false,
      fulfillments: [],
      new_fills: [],
    });
  };

  // Form submission
  const onSubmit = async (data: SOFormData) => {
    try {
      setError(null);
      if (formMode === "create") {
        const soPayload: SalesOrderCreate = {
          number: !!data.number ? data.number : undefined,
          customer_id: data.customer?.value.id!,
          location_id: data.location?.value.id!,
          order_terms: data.order_terms?.value.id! as PaymentTermType,
          order_date: data.order_date || today.toISOString(),
          requested_date: data.requested_date || tomorrow.toISOString(),
          rush_order: data.rush_order!,
          tax_rate: data.tax_rate || 0,
          ship_cost: data.ship_cost || 0,
          customer_po: data.customer_po!,
          selected_shipment_billing: data.ship_billing?.value
            .id! as CustomerShipmentBillingType,
          selected_shipping_method: data.ship_method?.value.id!,
          selected_billing_addr_id: data.bill_addr?.value.id!,
          selected_billing_addr_edits: billAddrEdits,
          selected_shipping_addr_id: data.ship_addr?.value.id!,
          selected_shipping_addr_edits: shipAddrEdits,
          sales_rep_id: data.sales_rep?.value.id,
          customer_contact_id: data.customer_contact?.value.id || undefined,
          line_items: data.line_items.map((dataLine) => {
            const linePayload: SOLineCreate = {
              item_id: dataLine.item?.value.id!,
              quantity_ordered: dataLine.quantity_ordered!,
              unit_cost: dataLine.eval_line ? 0 : dataLine.unit_cost!,
              uom_unit_id: dataLine.uom_unit?.value.id!,
              notes: dataLine.notes || undefined,
              requires_fulfillment: dataLine.requires_fulfillment,
              eval_line: dataLine.eval_line,
            };
            return linePayload;
          }),
        };
        console.log("SUBMITTED SALES ORDER", soPayload);
        // const dupeCheck = await checkDupeMutation.mutateAsync({
        //   cust_po: soPayload.customer_po,
        //   exclude_so: soPayload.number ? [soPayload.number] : [],
        // });
        // if (dupeCheck.success === false) {
        //   if (
        //     !window.confirm(
        //       `Duplicate PO Detected\nUsed in the following Orders:\n${dupeCheck.message.replaceAll(",", "\n")}`,
        //     )
        //   ) {
        //     return;
        //   }
        // }
        setDisplayProcessDialog(true);
        const new_so = await createSOMutation.mutateAsync(soPayload);
        const updated = await postProcSOMutation.mutateAsync({ id: new_so.id });
        setRecId(new_so.id);
      } else if (formMode === "fulfill") {
        if (!recId) {
          throw new Error("ID Required for Receiving.");
        }
        setDisplayProcessDialog(true);
        const fill_lines: SOLineFulfillment[] = [];
        let full_content: {
          [key: string]: { [key: string]: { [key: string]: number } };
        } = {};
        let insAmount: number = 0;
        data.line_items.forEach((soLine, soLineInd) => {
          const linePayload: SOLineFulfillment = {
            line_id: soLine.id!,
            mov_data: [],
            mark_line_complete: soLine.mark_complete,
          };
          let pkg_contents: string[] = [];
          if (
            (soLine.line_completed && soLine.new_fills.length === 0) ||
            (soLine.mark_complete && soLine.line_completed) ||
            (!soLine.mark_complete && soLine.new_fills.length === 0)
          ) {
            return true;
          }
          if (soLine.item?.label !== "TGSA") {
            if (full_content[soLine.item?.label!] === undefined) {
              full_content[soLine.item?.label!] = {};
            }
          }
          soLine.new_fills.forEach((newFill, newFillInd) => {
            const movPayload: InvMovCreate = {
              from_position_id: newFill.position.value.id,
              to_location_id: null,
              to_status_id: null,
              lot_id: null,
              uom_unit_id: newFill.uom.value.id,
              quantity: newFill.quantity,
              movement_type: "ORDER_FULFILLMENT",
              associated_id: soLine.id!,
              associated_table: "sales_order_line_items",
              expiration_date: null,
              release_rev: null,
              ncs_to_create: [],
              ncs_to_link: [],
              rft: null,
              rw: null,
              pd: null,
              notes: null,
            };
            insAmount += soLine.unit_cost! * newFill.quantity;
            if (
              full_content[soLine.item?.label!][newFill.position.value.lot] ===
              undefined
            ) {
              full_content[soLine.item?.label!][newFill.position.value.lot] =
                {};
            }
            if (
              full_content[soLine.item?.label!][newFill.position.value.lot][
                newFill.uom.label
              ] === undefined
            ) {
              full_content[soLine.item?.label!][newFill.position.value.lot][
                newFill.uom.label
              ] = 0;
            }
            full_content[soLine.item?.label!][newFill.position.value.lot][
              newFill.uom.label
            ] += newFill.quantity;
            pkg_contents.push(
              `${soLine.item?.label} - Lot: ${newFill.position.value.lot}`,
            );
            linePayload.mov_data.push(movPayload);
          });
          if (data.create_shipment && soLine.item?.label !== "TGSA") {
            console.log("CREATE SHIPMENT", soLine.item?.label);
            switch (soLine.item?.label) {
              case "TGDK110012":
                linePayload["nuc_pkg_id"] = "tgdk";
                break;
              case "TGCM1000":
                linePayload["nuc_pkg_id"] = "tgcm";
                break;
              case "TGCT120020":
                linePayload["nuc_pkg_id"] = "tgct20";
                break;
              case "TGCT120028":
                linePayload["nuc_pkg_id"] = "tgct28";
                break;
              case "TGPS0100":
                linePayload["nuc_pkg_id"] = "tgps";
                break;
              default:
                linePayload["nuc_pkg_id"] = undefined;
            }
            linePayload["package_contents"] = pkg_contents;
          }
          fill_lines.push(linePayload);
        });
        const soPayload: SOFulfillment = {
          line_items: fill_lines,
          create_shipment: data.create_shipment,
          full_content: full_content,
          insurance_amount: insAmount.toFixed(2),
        };
        await fulfillSOMutation.mutateAsync({ id: recId, data: soPayload });
      } else if (formMode === "edit") {
        if (!recId) {
          setError("Missing Order ID, unable to update. Contact IT.");
          return;
        }
        const newLines: SOLineCreate[] = [];
        const updateLines: SOLineUpdate[] = [];
        const deleteLines: string[] = [];
        data.line_items.forEach((soLine, lineIndex) => {
          if (soLine.is_new) {
            newLines.push({
              item_id: soLine.item?.value.id!,
              uom_unit_id: soLine.uom_unit?.value.id!,
              quantity_ordered: soLine.quantity_ordered!,
              requires_fulfillment: soLine.requires_fulfillment,
              eval_line: soLine.eval_line,
              unit_cost: soLine.eval_line ? 0 : soLine.unit_cost!,
              notes: soLine.notes || undefined,
            });
          } else if (soLine.is_delete) {
            deleteLines.push(soLine.id!);
          } else if (!!soLine.id && soLine.quantity_fulfilled === 0) {
            const origLine =
              refOrigData.current?.line_items.find(
                (line) => line.id === soLine.id,
              ) || null;
            if (origLine) {
              const linePayload: SOLineUpdate = {
                line_id: origLine.id,
              };
              if (soLine.item?.value.id !== origLine.item.id) {
                linePayload["item_id"] = soLine.item?.value.id;
              }
              if (soLine.uom_unit?.value.id !== origLine.uom_unit.id) {
                linePayload["uom_unit_id"] = soLine.uom_unit?.value.id;
              }
              if (
                soLine.quantity_ordered !==
                parseFloat(origLine.quantity_ordered)
              ) {
                linePayload["quantity_ordered"] = soLine.quantity_ordered!;
              }
              if (
                soLine.requires_fulfillment !== origLine.requires_fulfillment
              ) {
                linePayload["requires_fulfillment"] =
                  soLine.requires_fulfillment;
              }
              if (soLine.eval_line !== origLine.eval_line) {
                linePayload["eval_line"] = soLine.eval_line;
                linePayload["unit_cost"] = 0;
              }
              if (soLine.unit_cost !== parseFloat(origLine.unit_cost)) {
                linePayload["unit_cost"] = soLine.unit_cost!;
              }
              if (soLine.notes !== origLine.notes) {
                linePayload["notes"] = soLine.notes || undefined;
              }
              updateLines.push(linePayload);
            }
          }
        });
        const soPayload: any = { id: recId };
        if (data.number !== refOrigData.current?.number) {
          soPayload["number"] = data.number;
        }
        if (data.customer?.value.id !== refOrigData.current?.customer.id) {
          soPayload["customer_id"] = data.customer?.value.id;
        }
        if (data.location?.value.id !== refOrigData.current?.location.id) {
          soPayload["location_id"] = data.location?.value.id;
        }
        if (data.order_terms?.value.id !== refOrigData.current?.order_terms) {
          soPayload["order_terms"] = data.order_terms?.value.id;
        }
        if (data.order_date !== refOrigData.current?.order_date) {
          soPayload["order_date"] = data.order_date;
        }
        if (data.requested_date !== refOrigData.current?.requested_date) {
          soPayload["requested_date"] = data.requested_date;
        }
        if (data.rush_order !== refOrigData.current?.rush_order) {
          soPayload["rush_order"] = data.rush_order;
        }
        if (data.customer_po !== refOrigData.current?.customer_po) {
          soPayload["customer_po"] = data.customer_po;
        }
        if (
          data.ship_billing?.value.id !==
          refOrigData.current?.selected_shipment_billing
        ) {
          soPayload["selected_shipment_billing"] = data.ship_billing?.value.id;
        }
        if (
          data.ship_method?.value.id !==
          refOrigData.current?.selected_shipping_method
        ) {
          soPayload["selected_shipping_method"] = data.ship_method?.value.id;
        }
        if (
          data.ship_addr?.value.id !==
          refOrigData.current?.selected_shipping_addr.id
        ) {
          soPayload["selected_shipping_addr_id"] = data.ship_addr?.value.id;
        }
        if (
          data.bill_addr?.value.id !==
          refOrigData.current?.selected_billing_addr.id
        ) {
          soPayload["selected_billing_addr_id"] = data.bill_addr?.value.id;
        }
        soPayload["selected_billing_addr_edits"] = billAddrEdits;
        soPayload["selected_shipping_addr_edits"] = shipAddrEdits;
        if (data.sales_rep?.value.id !== refOrigData.current?.sales_rep?.id) {
          soPayload["sales_rep_id"] = data.sales_rep?.value.id;
        }
        soPayload["line_items_to_add"] = newLines;
        soPayload["line_items_to_update"] = updateLines;
        soPayload["line_item_ids_to_delete"] = deleteLines;
        setDisplayProcessDialog(true);
        await updateSOMutation.mutateAsync({ id: recId, data: soPayload });
      } else {
        return;
      }
    } catch (err: any) {
      console.error("Failed to save so:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to save so",
      );
    }
  };

  const handleCancel = () => {
    window.open("/orders/sales", "_self");
  };

  const handlePrintOpen = () => {
    const ordDate = new Date(refOrigData.current?.order_date!);
    const reqDate = refOrigData.current?.requested_date
      ? new Date(refOrigData.current?.requested_date)
      : undefined;
    const itemData: {
      number: string;
      name: string;
      quantity: string;
      uom: string;
      rate: string;
      total: string;
      eval: boolean;
    }[] = [];
    refOrigData.current?.line_items.forEach((soLine, lineIndex) => {
      let quant = soLine.quantity_ordered.toString();
      if (quant.split(".")[1] === "0000") {
        quant = quant.split(".")[0];
      }
      itemData.push({
        number: soLine.item.catalog_name!,
        name: soLine.item.name,
        quantity: quant,
        uom: soLine.uom_unit.unit_symbol,
        rate: soLine.eval_line ? "0.00" : Number(soLine.unit_cost).toFixed(2),
        total: soLine.eval_line
          ? "0.00"
          : Number(
              parseFloat(soLine.unit_cost) *
                parseFloat(soLine.quantity_ordered),
            ).toFixed(2),
        eval: soLine.eval_line,
      });
    });
    const tempDate = new Date(ordDate);
    tempDate.setDate(tempDate.getDate() + 1);
    const data: SOPrintData = {
      number: refOrigData.current?.number!,
      orderDate: `${
        ordDate.getMonth() + 1
      }/${ordDate.getDate()}/${ordDate.getFullYear()}`,
      ackShipDate: `${
        tempDate.getMonth() + 1
      }/${tempDate.getDate()}/${tempDate.getFullYear()}`,
      requestDate: reqDate
        ? `${
            reqDate.getMonth() + 1
          }/${reqDate.getDate()}/${reqDate.getFullYear()}`
        : undefined,
      custPO: refOrigData.current?.customer_po!,
      shipMethod: refOrigData.current?.selected_shipping_method!,
      pricing: {
        sub: subtotal.toFixed(2),
        tax: "0.00",
        ship: "TBD",
        total: grandTotal.toFixed(2),
      },
      terms: refOrigData.current?.order_terms!,
      customer: {
        name: refOrigData.current?.customer.name!,
        code: refOrigData.current?.customer.code!,
        billing: {
          name: refOrigData.current?.selected_billing_addr.name!,
          attn:
            billAddrEdits.attn ||
            refOrigData.current?.selected_billing_addr.attn ||
            "",
          addressee:
            billAddrEdits.addressee ||
            refOrigData.current?.selected_billing_addr.addressee ||
            "",
          line1:
            billAddrEdits.line_1 ||
            refOrigData.current?.selected_billing_addr.line_1!,
          line2:
            billAddrEdits.line_2 ||
            refOrigData.current?.selected_billing_addr.line_2,
          line3:
            billAddrEdits.line_3 ||
            refOrigData.current?.selected_billing_addr.line_3,
          city:
            billAddrEdits.city ||
            refOrigData.current?.selected_billing_addr.city!,
          state:
            billAddrEdits.state ||
            refOrigData.current?.selected_billing_addr.state!,
          country:
            billAddrEdits.country ||
            refOrigData.current?.selected_billing_addr.country!,
          zip:
            billAddrEdits.zip ||
            refOrigData.current?.selected_billing_addr.zip!,
        },
        shipping: {
          name: refOrigData.current?.selected_shipping_addr.name!,
          attn:
            shipAddrEdits.attn ||
            refOrigData.current?.selected_shipping_addr.attn ||
            "",
          addressee:
            shipAddrEdits.addressee ||
            refOrigData.current?.selected_shipping_addr.addressee ||
            "",
          line1:
            shipAddrEdits.line_1 ||
            refOrigData.current?.selected_shipping_addr.line_1!,
          line2:
            shipAddrEdits.line_2 ||
            refOrigData.current?.selected_shipping_addr.line_2,
          line3:
            shipAddrEdits.line_3 ||
            refOrigData.current?.selected_shipping_addr.line_3,
          city:
            shipAddrEdits.city ||
            refOrigData.current?.selected_shipping_addr.city!,
          state:
            shipAddrEdits.state ||
            refOrigData.current?.selected_shipping_addr.state!,
          country:
            shipAddrEdits.country ||
            refOrigData.current?.selected_shipping_addr.country!,
          zip:
            shipAddrEdits.zip ||
            refOrigData.current?.selected_shipping_addr.zip!,
        },
        contact: {
          first: refOrigData.current?.customer_contact?.first_name || "",
          last: refOrigData.current?.customer_contact?.last_name || "",
          phone: refOrigData.current?.customer_contact?.phone || "",
          email: refOrigData.current?.customer_contact?.email || "",
        },
      },
      items: itemData,
    };
    const htmlData = generateSalesOrderHTML(data);

    const printWindow = window.open(
      "",
      "_blank",
      "width=800,height=600,scrollbars=yes,resizable=yes",
    );

    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(htmlData);
      printWindow.document.close();

      printWindow.focus();
    } else {
      alert("Please allow popups for this site to enable printing.");
    }
  };

  const completeOrder = async () => {
    if (!recId) {
      setError("Missing ID, Cannot Complete Order");
      return;
    }
    try {
      await completeSOMutation.mutateAsync(recId);
      thisSORefetch();
    } catch (e: any) {
      setError("There was an error marking SO Complete");
      console.error(e);
      return;
    }
  };

  const voidOrder = async () => {
    if (!recId) {
      setError("Missing ID, Cannot VOID Order");
      return;
    }
    try {
      await voidSOMutation.mutateAsync(recId);
      thisSORefetch();
    } catch (e: any) {
      setError("There was an error marking SO VOID");
      console.error(e);
      return;
    }
  };

  if (!dataReady || formResetting) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
        <Typography variant="h5">
          {!dataReady
            ? "Loading Base Data..."
            : formResetting
              ? "Form Resetting..."
              : "Please Hold..."}
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            variant="outlined"
          >
            Back to Sales Orders
          </Button>
          <Typography variant="h4" component="h1">
            {formMode === "create"
              ? "Create Sales Order"
              : formMode === "view"
                ? `View SO ${thisSOData?.number || "Unkown SO"}`
                : formMode === "fulfill"
                  ? `Fulfill SO ${thisSOData?.number || "Unkown SO"}`
                  : `Edit SO ${thisSOData?.number || "Unknown SO"}`}
          </Typography>
          {formMode === "view" && (
            <Button
              variant="outlined"
              onClick={switchToCreateMode}
              startIcon={<AddIcon />}
            >
              Create New
            </Button>
          )}
          {formMode === "view" && recId && refOrigData.current && (
            <OrderStatusActions
              recordType={"sales_order"}
              recordId={recId}
              disabled={false}
              orderStatus={refOrigData.current.order_status}
              approvalStatus={refOrigData.current.approval_status}
              soStatus={refOrigData.current.fulfillment_status}
              mutation={statUpdateMutation}
              onStatusUpdateComplete={(response) => {
                if (response.success) {
                  window.open(
                    `/orders/sales/form?mode=view&id=${recId}`,
                    "_self",
                  );
                } else {
                  setError(response.message);
                }
              }}
            />
          )}
          {/* {(formMode === "view" || formMode === "edit") &&
            thisSOData &&
            !["COMPLETE", "VOID"].includes(thisSOData?.order_status) && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={completeOrder}
                  startIcon={<CheckIcon />}
                >
                  Complete
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={voidOrder}
                  startIcon={<CancelIcon />}
                >
                  VOID
                </Button>
              </Box>
            )} */}
          {formMode === "view" && (
            <Button
              variant="outlined"
              onClick={switchToEditMode}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          )}
          {formMode === "view" && (
            <Button
              variant="outlined"
              onClick={handlePrintOpen}
              startIcon={<PrintIcon />}
            >
              Print
            </Button>
          )}
          {formMode === "edit" && (
            <Button
              variant="outlined"
              onClick={switchToViewMode}
              startIcon={<VisibilityIcon />}
            >
              View
            </Button>
          )}
          {!(formMode === "fulfill") && !(formMode === "create") && (
            <Button
              variant="outlined"
              onClick={switchToFulfillMode}
              startIcon={<InventoryIcon />}
            >
              Fulfill
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => {
              console.log(getValues());
            }}
          >
            DEBUG PRINT
          </Button>
        </Stack>

        {/* Error Alert */}
        {(error ||
          thisSOError ||
          itemsError ||
          unitsError ||
          customerError ||
          salesRepsError ||
          locsError) && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error ||
              itemsError?.message ||
              unitsError?.message ||
              thisSOError?.message ||
              customerError?.message ||
              salesRepsError?.message ||
              locsError?.message ||
              "An unknown error occurred"}
          </Alert>
        )}

        {/* Form */}
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit(onSubmit, console.error)}>
            <Stack spacing={3} sx={{ width: "100%" }}>
              {formMode !== "create" && thisSOData && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Typography variant="h6">Order Status:</Typography>
                  <Chip
                    color={statusUtils.getPrimaryOrderStatusColor(
                      thisSOData.order_status,
                    )}
                    label={stringUtils.capFirstLetter(thisSOData.order_status)}
                    size="small"
                  />
                  <Typography variant="h6">Fulfillment Status:</Typography>
                  <Chip
                    color={statusUtils.getSoFulfillmentStatusColor(
                      thisSOData.fulfillment_status,
                    )}
                    label={stringUtils.capFirstLetter(
                      thisSOData.fulfillment_status,
                    )}
                    size="small"
                  />
                </Box>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumn: "repeat(2, 1fr)",
                  gridAutoRows: "auto",
                  columnGap: 1,
                  rowGap: 2,
                }}
              >
                <Box
                  sx={{
                    gridColumn: 1,
                    gridRow: 1,
                  }}
                >
                  <Controller
                    name="customer"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => {
                      return (
                        <Autocomplete
                          {...restField}
                          value={value}
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                          options={custOpts}
                          disabled={
                            custInfoLoading ||
                            (formMode !== "create" && formMode !== "edit")
                          }
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.value.id === value?.value.id
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Customer"
                              inputRef={customerInputRef}
                            />
                          )}
                        />
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 1,
                  }}
                >
                  <Controller
                    name="number"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <TextField
                        {...restField}
                        value={value || ""}
                        onChange={onChange}
                        label="SO Number"
                        fullWidth
                        disabled={
                          (formMode !== "create" && formMode !== "edit") ||
                          !overrideSOGen
                        }
                        error={!!errors.number}
                        helperText={
                          errors.number?.message || "SO Numbers must be unique."
                        }
                        variant={"outlined"}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip
                                  title={
                                    overrideSOGen
                                      ? "Lock SO Number"
                                      : formMode === "create"
                                        ? "Override SO# Generation"
                                        : "Update SO#"
                                  }
                                >
                                  <IconButton
                                    onClick={() =>
                                      setOverrideSOGen(!overrideSOGen)
                                    }
                                    edge="end"
                                    size="small"
                                    disabled={
                                      formMode !== "create" &&
                                      formMode !== "edit"
                                    }
                                  >
                                    {overrideSOGen ? (
                                      <LockIcon />
                                    ) : (
                                      <EditIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reset SO#">
                                  <IconButton
                                    onClick={() => {
                                      if (overrideSOGen)
                                        setOverrideSOGen(false);
                                      setValue("number", origSONum);
                                    }}
                                    edge="end"
                                    size="small"
                                    disabled={
                                      formMode !== "create" &&
                                      formMode !== "edit"
                                    }
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 1,
                    gridRow: 2,
                  }}
                >
                  <Controller
                    name="customer_po"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <TextField
                        {...restField}
                        value={value || ""}
                        onChange={onChange}
                        label="Customer PO"
                        fullWidth
                        disabled={formMode !== "create" && formMode !== "edit"}
                        error={!!errors.customer_po || !!dupeCustPO}
                        helperText={(() => {
                          let hText = "";
                          if (dupeCustPO) {
                            hText = "Duplicate Customer PO Detected";
                          }
                          if (!!errors.customer_po?.message) {
                            if (hText !== "") {
                              hText = `${hText}\n${errors.customer_po?.message}`;
                            } else {
                              hText = errors.customer_po?.message;
                            }
                          }
                          return hText;
                        })()}
                        variant={"outlined"}
                        onBlur={(e) => {
                          if (
                            ["create", "edit"].includes(formMode) &&
                            e.target.value !== ""
                          ) {
                            handleDupeCheck(e.target.value);
                          }
                        }}
                        slotProps={{
                          input: {
                            endAdornment: !!dupeCustPO ? (
                              <Tooltip
                                title={
                                  <div>
                                    Used In:
                                    <br />
                                    {dupeCustPO.split(",").map((v) => {
                                      return (
                                        <>
                                          {v}
                                          <br />
                                        </>
                                      );
                                    })}
                                  </div>
                                }
                                placement="bottom"
                              >
                                <ContentCopy color="error" fontSize="small" />
                              </Tooltip>
                            ) : undefined,
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 2,
                  }}
                >
                  <Controller
                    name="order_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Order Date"
                        value={field.value ? new Date(field.value) : null}
                        onChange={(date) => field.onChange(date?.toISOString())}
                        disabled={formMode !== "create" && formMode !== "edit"}
                        slotProps={{
                          textField: {
                            error: !!errors.order_date,
                            fullWidth: true,
                            helperText: errors.order_date?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gridColumn: 1,
                    gridRow: 3,
                  }}
                >
                  <AddressFieldOld
                    control={control}
                    fieldName={"bill_addr"}
                    selectLabel={"Billing Address"}
                    formMode={formMode}
                    watchedAddrOpt={watchedBillAddrOpt}
                    availAddr={availBillAddrOpts}
                    existingEdit={billAddrEdits}
                    setNewEdits={setBillAddrEdits}
                    worldLocContext={worldLocContext}
                    hasError={!!errors.bill_addr}
                    errorText={errors.bill_addr?.message}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 3,
                  }}
                >
                  <Controller
                    name="requested_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Requested Date"
                        value={field.value ? new Date(field.value) : null}
                        onChange={(date) =>
                          field.onChange(date?.toISOString() || "")
                        }
                        disabled={formMode !== "create" && formMode !== "edit"}
                        slotProps={{
                          textField: {
                            error: !!errors.requested_date,
                            fullWidth: true,
                            helperText: errors.requested_date?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 1,
                    gridRow: 4,
                  }}
                >
                  <Controller
                    name="location"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => {
                      return (
                        <Autocomplete
                          {...restField}
                          value={value}
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                          options={locOpts}
                          disabled={
                            formMode !== "create" && formMode !== "edit"
                          }
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.value.id === value?.value.id
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Location" />
                          )}
                        />
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 4,
                  }}
                >
                  <AddressFieldOld
                    control={control}
                    fieldName={"ship_addr"}
                    selectLabel={"Shipping Address"}
                    formMode={formMode}
                    watchedAddrOpt={watchedShipAddrOpt}
                    availAddr={availShipAddrOpts}
                    existingEdit={shipAddrEdits}
                    setNewEdits={setShipAddrEdits}
                    worldLocContext={worldLocContext}
                    hasError={!!errors.ship_addr}
                    errorText={errors.ship_addr?.message}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 1,
                    gridRow: 5,
                  }}
                >
                  <Controller
                    name="sales_rep"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => {
                      return (
                        <Autocomplete
                          {...restField}
                          value={value}
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                          options={salesRepOpts}
                          disabled={
                            formMode !== "create" && formMode !== "edit"
                          }
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.value.id === value?.value.id
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Centese Sales Rep" />
                          )}
                        />
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 5,
                  }}
                >
                  <Controller
                    name="ship_method"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => {
                      return (
                        <Autocomplete
                          {...restField}
                          value={value}
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                          options={shippingMethodOptions}
                          disabled={
                            formMode !== "create" && formMode !== "edit"
                          }
                          groupBy={(option) => option.value.provider}
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.value.id === value?.value.id
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Shipping Method" />
                          )}
                        />
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 1,
                    gridRow: 6,
                  }}
                >
                  <Controller
                    name="ship_billing"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => {
                      return (
                        <Autocomplete
                          {...restField}
                          value={value}
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                          options={customerShipmentBillingOptions}
                          disabled={
                            formMode !== "create" && formMode !== "edit"
                          }
                          getOptionLabel={(option) => option.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.value.id === value.value.id
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Bill Shipping To" />
                          )}
                        />
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 6,
                  }}
                >
                  <Controller
                    name="customer_contact"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => {
                      return (
                        <Autocomplete
                          {...restField}
                          value={value}
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                          options={contactOpts}
                          disabled={
                            formMode !== "create" && formMode !== "edit"
                          }
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.value.id === value?.value.id
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Customer Contact" />
                          )}
                        />
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 1,
                    gridRow: 7,
                  }}
                >
                  <Controller
                    name="order_terms"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => {
                      return (
                        <Autocomplete
                          {...restField}
                          value={value}
                          fullWidth
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                          options={paymentTermOptions}
                          disabled={
                            formMode !== "create" && formMode !== "edit"
                          }
                          getOptionLabel={(option) => option.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.value.id === value.value.id
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Order Terms" />
                          )}
                        />
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 7,
                  }}
                >
                  <Controller
                    name="rush_order"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                            disabled={
                              formMode !== "create" && formMode !== "edit"
                            }
                          />
                        }
                        label="Add Rush Fee?"
                      />
                    )}
                  />
                </Box>
              </Box>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "8%" }}>Row #</TableCell>
                      <TableCell sx={{ width: "20%" }}>Item</TableCell>
                      <TableCell sx={{ width: "20%" }}>Quantity</TableCell>
                      <TableCell sx={{ width: "20%" }}>Unit Cost</TableCell>
                      <TableCell sx={{ width: "20%" }}>Notes</TableCell>
                      <TableCell sx={{ width: "12%" }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gridTemplateRows: "auto auto auto",
                            rowGap: 1,
                            columnGap: 2,
                            bgcolor: "grey.200",
                            borderRadius: 2,
                            p: 1,
                            justifySelf: "flex-end",
                            textWrap: "nowrap",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              gridColumn: 1,
                              gridRow: 1,
                              textAlign: "start",
                            }}
                          >
                            Subtotal
                          </Typography>
                          <NumericDisplayField
                            value={subtotal}
                            decimalPlaces={2}
                            isCurrency={true}
                            typographyProps={{
                              variant: "h6",
                              sx: {
                                gridColumn: 2,
                                gridRow: 1,
                                textAlign: "end",
                              },
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              gridColumn: 1,
                              gridRow: 2,
                              textAlign: "start",
                              textWrap: "nowrap",
                            }}
                          >
                            {`Rush ${isRushOrder ? "10" : "0"}%`}
                          </Typography>
                          {isRushOrder ? (
                            <NumericDisplayField
                              value={rushTotal}
                              decimalPlaces={2}
                              isCurrency={true}
                              typographyProps={{
                                variant: "h6",
                                sx: {
                                  gridColumn: 2,
                                  gridRow: 2,
                                  textAlign: "end",
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="h6"
                              sx={{
                                gridColumn: 2,
                                gridRow: 2,
                                textAlign: "end",
                              }}
                            >
                              {"N/A"}
                            </Typography>
                          )}
                          <Typography
                            variant="h6"
                            sx={{
                              gridColumn: 1,
                              gridRow: 3,
                              textAlign: "start",
                            }}
                          >
                            Total
                          </Typography>
                          <NumericDisplayField
                            value={grandTotal}
                            decimalPlaces={2}
                            isCurrency={true}
                            typographyProps={{
                              variant: "h6",
                              sx: {
                                gridColumn: 2,
                                gridRow: 3,
                                textAlign: "end",
                              },
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {soLineItems.map((field, index) => (
                      <SOLine
                        key={field.id}
                        fieldData={field}
                        index={index}
                        control={control}
                        setValue={setValue}
                        remove={removeSOLine}
                        allItems={itemOpts}
                        allUoms={uomOpts}
                        formMode={formMode}
                        errors={errors}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {!(formMode === "fulfill") && !(formMode === "view") && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addSOLine}
                  >
                    Add Line
                  </Button>
                </Box>
              )}

              {/* Form Actions */}
              {!(formMode === "view") && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    pt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? "Saving..."
                      : formMode === "create"
                        ? "Create Sales Order"
                        : formMode === "fulfill"
                          ? "Fulfill Sales Order"
                          : "Update Sales Order"}
                  </Button>
                </Box>
              )}
              <Dialog
                open={displayProcessDialog}
                onClose={(e, r) => {
                  if (formMode !== "fulfill") {
                    return;
                  } else {
                    handleProcessDialogClose(e, r);
                  }
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
              >
                <DialogContent>
                  {formMode === "create" ? (
                    <Stack direction="column" gap={3}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">Order</Typography>
                        {createSOMutation.isPending ? (
                          <CircularProgress />
                        ) : createSOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">Box/Slack</Typography>
                        {createSOMutation.isPending ||
                        postProcSOMutation.isPending ? (
                          <CircularProgress />
                        ) : postProcSOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      {createSOMutation.isPending ||
                      postProcSOMutation.isPending ? (
                        <Typography variant="h5">
                          Form will navigate to "View" mode once requests are
                          completed...
                        </Typography>
                      ) : (
                        <Typography variant="h5">
                          Requests completed, returning to "View" mode...
                        </Typography>
                      )}
                    </Stack>
                  ) : formMode === "fulfill" ? (
                    <Stack direction="column" gap={3}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">Fulfilling Order</Typography>
                        {fulfillSOMutation.isPending ? (
                          <CircularProgress />
                        ) : fulfillSOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      {/* <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">Box/Slack</Typography>
                        {createSOMutation.isPending ||
                        postProcSOMutation.isPending ? (
                          <CircularProgress />
                        ) : postProcSOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box> */}
                      {fulfillSOMutation.isPending ? (
                        <Typography variant="h5">
                          Will return to "List" page once requests are
                          completed...
                        </Typography>
                      ) : (
                        <Typography variant="h5">
                          Requests completed, returning to "List" page...
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Stack direction="column" gap={3}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">Updating Order</Typography>
                        {updateSOMutation.isPending ? (
                          <CircularProgress />
                        ) : updateSOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      {updateSOMutation.isPending ? (
                        <Typography variant="h5">
                          Form will navigate to "View" mode once requests are
                          completed...
                        </Typography>
                      ) : (
                        <Typography variant="h5">
                          Requests completed, returning to "View" mode...
                        </Typography>
                      )}
                    </Stack>
                  )}
                </DialogContent>
              </Dialog>
            </Stack>
          </form>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};
